import { applyOnlineVisitPatientDisplayBufferMs } from '@/modules/booking/utils/onlineVisitPatientDisplayTime';

const TEHRAN_TZ = 'Asia/Tehran';
const DAY_MS = 24 * 60 * 60 * 1000;

type TehranClock = {
  hour: number;
  minute: number;
  second: number;
};

export const getTehranClock = (ms: number): TehranClock => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TEHRAN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));

  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value ?? 0);

  return {
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
};

export type DayPeriodFa = 'بامداد' | 'صبح' | 'ظهر' | 'عصر' | 'شب';

/**
 * Spoken part of day for a 24h Tehran hour (used with a 12-hour clock). «عصر» covers the whole
 * afternoon: «بعدازظهر» made the CTA too long for a 360px phone (the button overflowed its card).
 */
export const dayPeriodFa = (hour: number): DayPeriodFa => {
  if (hour < 5) return 'بامداد';
  if (hour < 12) return 'صبح';
  if (hour < 14) return 'ظهر';
  if (hour < 20) return 'عصر';
  return 'شب';
};

/** «10:15» / «9» on a 12-hour clock (0 → 12). No «ساعت»: same reason, the button width. */
const clockFa = ({ hour, minute }: { hour: number; minute: number }): string => {
  const h12 = hour % 12 || 12;
  return minute === 0 ? String(h12) : `${h12}:${String(minute).padStart(2, '0')}`;
};

const toLatinDigits = (value: string): string =>
  value
    .replace(/[۰-۹]/g, digit => String(digit.charCodeAt(0) - '۰'.charCodeAt(0)))
    .replace(/[٠-٩]/g, digit => String(digit.charCodeAt(0) - '٠'.charCodeAt(0)));

export const tehranGregorianDateKey = (ms: number): string => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TEHRAN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ms));

  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)?.value ?? '';
  return `${read('year')}-${read('month')}-${read('day')}`;
};

export const isSameTehranCalendarDay = (leftMs: number, rightMs: number): boolean =>
  tehranGregorianDateKey(leftMs) === tehranGregorianDateKey(rightMs);

/** Days since epoch of the Tehran calendar date (for whole-day differences, DST-free). */
const tehranDayNumber = (ms: number): number => {
  const [year, month, day] = tehranGregorianDateKey(ms).split('-').map(Number);
  return Date.UTC(year, month - 1, day) / DAY_MS;
};

const WEEKDAYS_FA = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'];

const tehranWeekdayFa = (ms: number): string => WEEKDAYS_FA[new Date(tehranDayNumber(ms) * DAY_MS).getUTCDay()];

/** «20 مهر» (Jalali day and month, latin digits). */
export const formatJalaliDayMonthFa = (ms: number): string => {
  const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    timeZone: TEHRAN_TZ,
    day: 'numeric',
    month: 'long',
  }).formatToParts(new Date(ms));

  const day = toLatinDigits(parts.find(part => part.type === 'day')?.value ?? '');
  const month = parts.find(part => part.type === 'month')?.value ?? '';
  return `${day} ${month}`;
};

/**
 * When the online visit starts, as a patient would say it, relative to now. `displayMs` is the
 * already-buffered time (see applyOnlineVisitPatientDisplayBufferMs).
 * - under an hour: «حدود 20 دقیقه دیگر»
 * - tonight (evening, or past midnight while it is still evening now): «امشب حدود 10:15»,
 *   «امشب حدود 12:30 بامداد» — never «شب امشب»
 * - today: «امروز حدود 3:30 عصر»
 * - tomorrow: «فردا حدود 9 صبح», «فردا شب حدود 9»
 * - within a week: «پنج‌شنبه حدود 10 صبح»; later: «20 مهر حدود 10 صبح»
 * Longest outputs («20 اردیبهشت حدود 10:30 صبح», «چهارشنبه حدود 12:30 بامداد») were checked to fit
 * the button next to its arrow on a 360px-wide phone.
 */
export const describeOnlineVisitStartFa = (displayMs: number, nowMs: number): string => {
  const minutesAway = Math.ceil((displayMs - nowMs) / (5 * 60 * 1000)) * 5;
  if (minutesAway < 60) return `حدود ${Math.max(minutesAway, 5)} دقیقه دیگر`;

  const clock = getTehranClock(displayMs);
  const period = dayPeriodFa(clock.hour);
  const at = `حدود ${clockFa(clock)}`;
  const days = tehranDayNumber(displayMs) - tehranDayNumber(nowMs);
  const nowIsEvening = getTehranClock(nowMs).hour >= 17;

  const tonight = (days === 0 && (period === 'شب' || period === 'بامداد')) || (days === 1 && period === 'بامداد' && nowIsEvening);
  if (tonight) return period === 'بامداد' ? `امشب ${at} بامداد` : `امشب ${at}`;
  if (days === 0) return `امروز ${at} ${period}`;

  const dayWord = days === 1 ? 'فردا' : days < 7 ? tehranWeekdayFa(displayMs) : formatJalaliDayMonthFa(displayMs);
  return period === 'شب' ? `${dayWord} شب ${at}` : `${dayWord} ${at} ${period}`;
};

export const ONLINE_VISIT_CTA_BASE = 'شروع ویزیت آنلاین';

export const buildOnlineVisitCtaText = (freeturnMs: number | null, nowMs = Date.now()): string => {
  if (freeturnMs == null) return ONLINE_VISIT_CTA_BASE;
  // A slot already in the past means «as soon as possible»: count from now.
  const displayMs = applyOnlineVisitPatientDisplayBufferMs(Math.max(freeturnMs, nowMs));
  return `${ONLINE_VISIT_CTA_BASE} ${describeOnlineVisitStartFa(displayMs, nowMs)}`;
};

export const onlineVisitCtaButtonProps = (
  freeturnMs: number | null,
  nowMs = Date.now(),
): { children2: string } | undefined => {
  if (freeturnMs == null) return undefined;
  return { children2: buildOnlineVisitCtaText(freeturnMs, nowMs) };
};
